import { TabList, Tab, SelectTabEvent, SelectTabData } from "@fluentui/react-components";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./AnalysisPanel.module.css";

import { SupportingContent } from "../../top-bar/SupportingContent";
import { ChatAppResponse } from "../../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { useMsal } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { getHeaders } from "../../../api";
import { useLogin } from "../../../utils/authConfig";
import { getToken } from "../../../utils/authConfig";
import { useState, useEffect, useMemo, useRef } from "react";

interface Props {
  className: string;
  activeTab: AnalysisPanelTabs;
  onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
  activeCitation: string | undefined;
  citationHeight: string;
  answer: ChatAppResponse;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

function getSuffixFromUrl(fetchUrl: string): string {
  try {
    const u = new URL(fetchUrl);
    const filename = u.searchParams.get("filename") || "";
    const decoded = decodeURIComponent(filename);
    const dot = decoded.lastIndexOf(".");
    return dot > 0 ? decoded.slice(dot + 1).toLowerCase() : "";
  } catch {
    return "";
  }
}

// Downloaded citation files by URL. Citations of the same file differ only in the page anchor,
// so the file is downloaded once; the oldest files are released beyond CITATION_CACHE_SIZE.
const CITATION_CACHE_SIZE = 10;
const citationFiles = new Map<string, Promise<string>>();

const loadCitationFile = (
  fetchUrl: string,
  client: IPublicClientApplication | undefined,
): Promise<string> => {
  const cached = citationFiles.get(fetchUrl);
  if (cached) {
    citationFiles.delete(fetchUrl); // re-insert: most recently used last
    citationFiles.set(fetchUrl, cached);
    return cached;
  }
  const request = (async () => {
    const token = client ? await getToken(client) : undefined;
    const headers = await getHeaders(token);
    const response = await fetch(fetchUrl, { method: "GET", headers: { ...headers } });
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    return URL.createObjectURL(await response.blob());
  })();
  citationFiles.set(fetchUrl, request);
  request.catch(() => citationFiles.delete(fetchUrl));
  while (citationFiles.size > CITATION_CACHE_SIZE) {
    const [oldestUrl, oldest] = citationFiles.entries().next().value as [string, Promise<string>];
    citationFiles.delete(oldestUrl);
    oldest.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);
  }
  return request;
};

export const AnalysisPanel = ({
  answer,
  activeTab,
  activeCitation,
  citationHeight,
  className,
  onActiveTabChanged,
}: Props) => {
  const isDisabledThoughtProcessTab: boolean =
    !answer.choices[0].context.thoughts;
  const isDisabledSupportingContentTab: boolean =
    !answer.choices[0].context.data_points;
  const isDisabledCitationTab: boolean = !activeCitation;

  const sanitizedThoughts = DOMPurify.sanitize(
    answer.choices[0].context.thoughts!,
  );

  const [citation, setCitation] = useState("");
  const [suffix, setSuffix] = useState<string>("");

  const client = useLogin ? useMsal().instance : undefined;

  // Latest citation request; a slower earlier download must not replace a newer one.
  const citationRequest = useRef(0);

  const fetchCitation = async () => {
    const requestId = ++citationRequest.current;
    if (!activeCitation) {
      setSuffix("");
      setCitation("");
      return;
    }
    const [fetchUrl, hashParam] = activeCitation.split("&hash=");
    const originalHash = hashParam ? decodeURIComponent(hashParam) : "";
    try {
      const objectUrl = await loadCitationFile(fetchUrl, client);
      if (requestId !== citationRequest.current) return;
      setSuffix(getSuffixFromUrl(fetchUrl));
      setCitation(originalHash ? objectUrl + originalHash : objectUrl);
    } catch (e) {
      if (requestId !== citationRequest.current) return;
      console.error("Failed to load citation", e);
      setSuffix("");
      setCitation("");
    }
  };

  useEffect(() => {
    fetchCitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCitation]);

  const citationView = useMemo(() => {
    if (!citation) return null;

    if (suffix === "pdf") {
      return (
        <iframe
          title="Citation"
          src={citation}
          width="100%"
          height={citationHeight}
        />
      );
    }

    if (suffix === "msg") {
      // Simple HTML view for .msg rendered via ObjectURL (assuming backend converts to HTML or serves text/html)
      return (
        <iframe
          title="Email"
          src={citation}
          width="100%"
          height={citationHeight}
        />
      );
    }

    if (["png", "jpg", "jpeg"].includes(suffix)) {
      return (
        <iframe
          title="Image"
          src={citation}
          width="100%"
          height={citationHeight}
        />
      );
    }

    return (
      <div className={styles.unsupportedBanner}>
        This document type is not supported for inline display.
      </div>
    );
  }, [citation, suffix, citationHeight]);

  return (
    <div className={className}>
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data: SelectTabData) =>
          onActiveTabChanged(data.value as AnalysisPanelTabs)
        }
      >
        <Tab
          value={AnalysisPanelTabs.ThoughtProcessTab}
          disabled={isDisabledThoughtProcessTab}
        >
          Thought process
        </Tab>
        <Tab
          value={AnalysisPanelTabs.SupportingContentTab}
          disabled={isDisabledSupportingContentTab}
        >
          Supporting content
        </Tab>
        <Tab
          value={AnalysisPanelTabs.CitationTab}
          disabled={isDisabledCitationTab}
        >
          Citation
        </Tab>
      </TabList>

      {activeTab === AnalysisPanelTabs.ThoughtProcessTab && (
        <div className={styles.thoughtProcess}>
          <ReactMarkdown
            children={sanitizedThoughts}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
          />
        </div>
      )}

      {activeTab === AnalysisPanelTabs.SupportingContentTab && (
        <SupportingContent
          supportingContent={answer.choices[0].context.data_points}
        />
      )}

      {activeTab === AnalysisPanelTabs.CitationTab && citationView}
    </div>
  );
};
